import { zodResolver } from "@hookform/resolvers/zod";
import { Save, UserPlus } from "lucide-react";
import { type DefaultValues, useForm } from "react-hook-form";
import { FormDialog } from "@/shared/components/FormDialog";
import { RaidRoleSelectItems } from "@/shared/components/RaidRoleSelectItems";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/ui/form";
import { Input } from "@/shared/components/ui/input";
import {
  Select,
  SelectContent,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { WowClassSelectItems } from "@/shared/components/WowClassSelectItems";
import type { RaidRole } from "@/shared/lib/roles";
import { useResetOnOpen } from "@/shared/lib/useResetOnOpen";
import type { WowClass } from "@/shared/lib/wowClass";
import { useCreateStandIn, useUpdateStandIn } from "../api/setup.mutations";
import { standInUpdate } from "../setup.overrides";
import {
  type StandInFormInput,
  type StandInFormValues,
  standInSchema,
} from "../setup.schemas";
import type { SetupPlayer } from "../setup.types";

// Class/role stay null until chosen — the selects show their placeholder.
function seedValues(player?: SetupPlayer): DefaultValues<StandInFormInput> {
  return {
    name: player?.name ?? "",
    wowClass: (player?.wowClass ?? null) as WowClass | null,
    raidRole: (player?.raidRole ?? null) as RaidRole | null,
  };
}

export function StandInDialog({
  open,
  onOpenChange,
  raidId,
  player,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  raidId: string;
  // Present → edit; absent → create.
  player?: SetupPlayer;
}) {
  const mode = player ? "edit" : "create";

  const form = useForm<StandInFormInput, unknown, StandInFormValues>({
    resolver: zodResolver(standInSchema),
    defaultValues: seedValues(player),
  });

  useResetOnOpen(open, () => form.reset(seedValues(player)));

  const create = useCreateStandIn(raidId);
  const update = useUpdateStandIn(raidId);

  const onSubmit = (values: StandInFormValues) => {
    // Mutations surface their own errors via the shared dialog; close only on success.
    if (mode === "create") {
      create.mutate(values, { onSuccess: () => onOpenChange(false) });
    } else if (player) {
      update.mutate(standInUpdate(player, values), {
        onSuccess: () => onOpenChange(false),
      });
    }
  };

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={mode === "create" ? "Ny stand-in" : "Redigera stand-in"}
      description={
        mode === "create"
          ? "Lägg till spelare för endast denna raid"
          : undefined
      }
      form={form}
      onSubmit={onSubmit}
      submitting={create.isPending || update.isPending}
      submitLabel={mode === "create" ? "Lägg till" : "Spara"}
      submitIcon={mode === "create" ? <UserPlus /> : <Save />}
    >
      <FormField
        control={form.control}
        name="name"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Namn</FormLabel>
            <FormControl>
              <Input maxLength={50} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="raidRole"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Roll</FormLabel>
            <Select
              value={field.value ?? undefined}
              onValueChange={field.onChange}
            >
              <FormControl>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Välj roll" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <RaidRoleSelectItems />
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="wowClass"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Klass</FormLabel>
            <Select
              value={field.value ?? undefined}
              onValueChange={field.onChange}
            >
              <FormControl>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Välj klass" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <WowClassSelectItems />
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
    </FormDialog>
  );
}
